import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { WorkItem } from '@/types'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { getWorkList } from '@/lib/work'
import WorkCard from '@/components/landing/work-card'

interface WorkPageProps {
    work: WorkItem[]
}

const WorkPage: FC<WorkPageProps> = ({ work = [] }): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Work'
                description='Case studies of AI UGC ads built by Unslop Studio: human-written hooks, real products and no slop.'
            />

            <section id='work-intro' className='border-b border-ink/10'>
                <div className='mx-auto max-w-7xl px-5 pt-16 pb-14 sm:px-8 lg:pt-24'>
                    <p className='font-mono text-xs uppercase tracking-[0.2em] text-ink/55'>
                        Case studies
                    </p>
                    <h1 className='mt-4 max-w-4xl font-heading text-[clamp(3rem,7vw,6rem)] leading-[0.92] tracking-tight text-ink'>
                        The work. <span className='italic text-signal'>Zero slop.</span>
                    </h1>
                    <p className='mt-6 max-w-2xl text-lg leading-relaxed text-ink/70'>
                        Real briefs, real products and the thinking behind every hook. Open any case study to see what we made and how it performed.
                    </p>
                </div>
            </section>

            <section id='work-grid' className='py-16 sm:py-24'>
                <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                    {work.length > 0 ? (
                        <div className='grid grid-cols-1 gap-x-8 gap-y-16 min-[480px]:grid-cols-2 lg:grid-cols-3'>
                            {work.map(item => <WorkCard key={item.id} item={item} />)}
                        </div>
                    ) : (
                        <p className='rounded-3xl border border-dashed border-ink/20 p-10 text-center text-ink/60'>
                            New case studies are on the way. Send a brief and we will share samples for your category.
                        </p>
                    )}
                </div>
            </section>

            <section id='work-cta' className='pb-24'>
                <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                    <div className='flex flex-col items-start justify-between gap-8 rounded-3xl bg-ink p-8 text-bone sm:p-12 md:flex-row md:items-center'>
                        <h2 className='max-w-xl font-heading text-4xl leading-tight sm:text-5xl'>
                            Want your product in the next case study?
                        </h2>
                        <Link
                            href='/#contact'
                            className='inline-flex shrink-0 items-center gap-2 rounded-full bg-signal px-6 py-3.5 text-sm font-semibold text-ink transition-transform hover:-translate-y-0.5 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-bone'
                        >
                            Start a brief
                            <ArrowUpRight className='size-4' aria-hidden='true' />
                        </Link>
                    </div>
                </div>
            </section>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<WorkPageProps> = async () => {
    try {
        const work = await getWorkList()
        return { props: { work }, revalidate: 60 }
    }
    catch (error) {
        console.error('Could not load work:', error instanceof Error ? error.message : error)
        return { props: { work: [] }, revalidate: 30 }
    }
}

export default WorkPage
